import React from 'react'
import cn from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'

const Nav = styled.nav`
  display: flex;
  padding: 0 ${rem(24)} ${rem(24)};
  pointer-events: none;

  &.background-solid {
    background-color: ${props => props.theme.palette.neutral.default.eight};
    pointer-events: auto;
  }
`

const Pill = styled.div`
  display: flex;
  flex: 1;
  align-items: stretch;
  padding: ${rem(4)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  box-shadow: ${props => props.theme.shadow.flat.small};
  pointer-events: auto;

  /* Frosted glass over the page content where the browser supports it, opaque otherwise */
  @supports (background-color: color-mix(in srgb, red 50%, transparent)) {
    background-color: color-mix(in srgb, ${props => props.theme.palette.neutral.default.seven} 85%, transparent);
    -webkit-backdrop-filter: blur(20px);
    backdrop-filter: blur(20px);
  }
`

export type AppNavigationBarBackground = 'transparent' | 'solid'

interface Props extends React.HTMLAttributes<HTMLElement> {
  background?: AppNavigationBarBackground
}

/**
 * Floating pill that lays out a row of `AppNavigationTab`s.
 *
 * Purely visual: it does not position itself, so fix or stick it from the caller.
 * Give it an `aria-label` when the page has more than one `nav` landmark.
 */
const AppNavigationBar = React.forwardRef<HTMLElement, Props>((props, ref) => {
  const { background = 'transparent', className, children, ...navProps } = props

  return <Nav {...navProps} ref={ref} className={cn(className, `background-${background}`)}>
    <Pill>{children}</Pill>
  </Nav>
})

AppNavigationBar.displayName = 'AppNavigationBar'

export default AppNavigationBar
